import { apiHost } from "./api-host";

const QUEUE_STORAGE_KEY = "gg-audit-pending";
const MAX_QUEUE_SIZE = 50;

// Events that often precede navigation — prefer sendBeacon / keepalive delivery
const RELIABLE_ACTIONS = new Set([
  "login",
  "logout",
  "link_click",
  "file_download",
]);

const RESOURCE_SEGMENTS = new Set([
  "patients",
  "patient",
  "records",
  "record",
  "users",
  "user",
  "encounters",
  "encounter",
  "appointments",
  "appointment",
  "documents",
  "document",
  "reports",
  "report",
  "orders",
  "order",
  "claims",
  "claim",
  "labs",
  "lab",
  "results",
  "result",
]);

// Maps hipaa_config.fields values to the keys we send in the request body
const FIELD_KEY_MAP = {
  email: "user_email",
  name: "user_name",
  ip: "ip_address",
  session_id: "session_id",
  page: "page",
  device: "device",
  element: "element",
  form_name: "form_name",
};

let _config = null;
let _siteId = null;
let _sessionId = null;
let _listeners = [];
let _initialized = false;
let _pageHideHandler = null;

function createSessionId() {
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  return (
    Math.random()
      .toString(36)
      .slice(2) + Date.now().toString(36)
  );
}

function getOrCreateSessionId() {
  let sid = sessionStorage.getItem("gg-audit-session");
  if (!sid) {
    sid = createSessionId();
    sessionStorage.setItem("gg-audit-session", sid);
  }
  return sid;
}

function getSiteUri() {
  return window.location.pathname.slice(1).split("/")[0];
}

function getViewerToken() {
  const siteUri = getSiteUri();
  if (!siteUri) return null;
  return window.localStorage.getItem(`gg-${siteUri}-token`);
}

function buildAuthHeaders() {
  const headers = { "Content-Type": "application/json" };
  const token = getViewerToken();
  if (token) {
    headers.Authorization = token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`;
  }
  return headers;
}

function formatElement(extra = {}) {
  const parts = [];
  if (extra.outcome) parts.push(`outcome:${extra.outcome}`);
  if (extra.reason) parts.push(`reason:${String(extra.reason).slice(0, 40)}`);
  if (extra.element) parts.push(String(extra.element));
  if (!parts.length) return null;
  return parts.join("; ").slice(0, 100);
}

// Reads viewer identity from common localStorage keys. Server prefers JWT claims
// when available; this is a fallback for sites that store user info in localStorage
// outside the token (e.g. userEmail / userName as plain keys).
function readIdentityFromLocalStorage() {
  const emailKeys = ["userEmail", "user_email", "email", "user-email"];
  const nameKeys = [
    "userName",
    "user_name",
    "name",
    "username",
    "user-name",
    "fullName",
    "full_name",
  ];

  let email = null;
  let name = null;

  try {
    for (const key of emailKeys) {
      const v = window.localStorage.getItem(key);
      if (v && typeof v === "string") {
        email = v;
        break;
      }
    }
    for (const key of nameKeys) {
      const v = window.localStorage.getItem(key);
      if (v && typeof v === "string") {
        name = v;
        break;
      }
    }
    // Some sites store the whole user object as JSON under a 'user' key
    if (!email || !name) {
      const userJson = window.localStorage.getItem("user");
      if (userJson) {
        try {
          const u = JSON.parse(userJson);
          if (!email && u && typeof u.email === "string") email = u.email;
          if (!name && u) {
            name = u.name || u.username || u.user_name || u.fullName || null;
          }
        } catch {
          /* not JSON */
        }
      }
    }
  } catch {
    /* localStorage unavailable */
  }

  return { email, name };
}

function buildPayload(action, extra = {}) {
  const enabledFields = _config.fields || [];
  const payload = { site_id: _siteId, action };

  // Server resolves identity from JWT first; these are fallbacks read from localStorage.
  const attemptEmail =
    action === "login" && extra.outcome === "failed" && extra.attemptEmail
      ? String(extra.attemptEmail).slice(0, 500)
      : null;

  const lsIdentity = readIdentityFromLocalStorage();
  const wuser = window.user || {};

  const available = {
    user_email: attemptEmail || lsIdentity.email || wuser.email || null,
    user_name: lsIdentity.name || wuser.name || wuser.username || null,
    session_id: _sessionId,
    page: window.location.pathname,
    device: navigator.userAgent,
    element: formatElement(extra),
    form_name: extra.form_name || null,
  };

  for (const configField of enabledFields) {
    const key = FIELD_KEY_MAP[configField];
    if (key && available[key] !== null && available[key] !== undefined) {
      payload[key] = available[key];
    }
  }

  return payload;
}

function loadQueue() {
  try {
    return JSON.parse(sessionStorage.getItem(QUEUE_STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveQueue(queue) {
  try {
    sessionStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // storage full or unavailable
  }
}

function enqueue(payload) {
  const queue = loadQueue();
  queue.push(payload);
  while (queue.length > MAX_QUEUE_SIZE) queue.shift();
  saveQueue(queue);
}

function beaconDeliver(payload) {
  // sendBeacon cannot attach Authorization headers — only used when no JWT is present
  if (getViewerToken()) return false;
  if (typeof navigator === "undefined" || !navigator.sendBeacon) return false;
  try {
    return navigator.sendBeacon(
      `${apiHost}/audit-log`,
      new Blob([JSON.stringify(payload)], { type: "application/json" }),
    );
  } catch {
    return false;
  }
}

async function deliverPayload(payload, action) {
  const reliable = RELIABLE_ACTIONS.has(action);
  const headers = buildAuthHeaders();
  const hasAuth = !!headers.Authorization;

  if (reliable && !hasAuth && beaconDeliver(payload)) {
    return true;
  }

  try {
    const res = await fetch(`${apiHost}/audit-log`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
      keepalive: reliable || hasAuth,
    });
    return res.ok;
  } catch {
    return false;
  }
}

function flushQueueBeacon() {
  if (!_config?.enabled || !_siteId) return;
  const queue = loadQueue();
  if (!queue.length) return;

  const remaining = [];
  for (const payload of queue) {
    if (!beaconDeliver(payload)) remaining.push(payload);
  }
  saveQueue(remaining);
}

async function flushQueue() {
  if (!_config?.enabled || !_siteId) return;
  const queue = loadQueue();
  if (!queue.length) return;

  const remaining = [];
  for (const payload of queue) {
    const action = payload.action || "";
    try {
      const ok = await deliverPayload(payload, action);
      if (!ok) remaining.push(payload);
    } catch {
      remaining.push(payload);
    }
  }
  saveQueue(remaining);
}

function send(action, extra = {}) {
  if (!_config || !_config.enabled) return;
  const enabledEvents = _config.events || [];
  if (!enabledEvents.includes(action)) return;

  const payload = buildPayload(action, extra);

  deliverPayload(payload, action)
    .then((ok) => {
      if (!ok) enqueue(payload);
    })
    .catch(() => enqueue(payload));
}

// Strip query strings — they may contain PHI in URL params
function sanitizeApiUrl(url) {
  if (!url) return "unknown";
  try {
    const parsed = new URL(url, window.location.href);
    return parsed.pathname.slice(0, 80);
  } catch {
    return String(url)
      .split("?")[0]
      .slice(0, 80);
  }
}

// Infer resource type/id from common REST path patterns (metadata only)
function extractResourceHint(pathname) {
  const segments = pathname.split("/").filter(Boolean);
  const idLike = /^[a-zA-Z0-9_-]{1,64}$/;

  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i].toLowerCase();
    const next = segments[i + 1];
    if (RESOURCE_SEGMENTS.has(seg) && idLike.test(next)) {
      return `${seg}:${next}`;
    }
  }

  if (segments.length >= 2) {
    const prev = segments[segments.length - 2].toLowerCase();
    const last = segments[segments.length - 1];
    if (RESOURCE_SEGMENTS.has(prev) && idLike.test(last)) {
      return `${prev}:${last}`;
    }
  }

  return null;
}

function buildApiElement(httpMethod, safePath) {
  const hint = extractResourceHint(safePath);
  const base = `api:${httpMethod} ${safePath}`;
  return hint ? `${base}; resource:${hint}` : base;
}

// Log remote API access using existing event types (no API schema changes).
// GET → link_click (fallback: page_view); mutating methods → form_submit.
export function logApiAccess(
  method,
  url,
  outcome,
  statusCode,
  { formLabel } = {},
) {
  if (!_config?.enabled) return;

  const httpMethod = (method || "GET").toUpperCase();
  const safePath = sanitizeApiUrl(url);
  const enabledEvents = _config.events || [];
  const extra = {
    outcome: outcome || "success",
    reason: statusCode != null ? `status:${statusCode}` : undefined,
    element: buildApiElement(httpMethod, safePath),
  };

  if (httpMethod === "GET" || httpMethod === "HEAD") {
    if (enabledEvents.includes("link_click")) {
      send("link_click", extra);
    } else if (enabledEvents.includes("page_view")) {
      send("page_view", extra);
    }
    return;
  }

  if (enabledEvents.includes("form_submit")) {
    send("form_submit", {
      ...extra,
      form_name: (formLabel || `api:${httpMethod} ${safePath}`).slice(0, 100),
    });
  }
}

// Extensions we treat as downloadable files when present in a link href
const FILE_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "csv",
  "txt",
  "rtf",
  "zip",
  "rar",
  "7z",
  "gz",
  "tar",
  "png",
  "jpg",
  "jpeg",
  "gif",
  "svg",
  "webp",
  "mp4",
  "mov",
  "avi",
  "mp3",
  "wav",
  "json",
  "xml",
];

function normalizeClassToken(value) {
  if (!value || typeof value !== "string") return "";
  return value.trim().replace(/^\./, "");
}

function shouldLogButtonClick(el) {
  const mode =
    _config?.button_log_mode ||
    (normalizeClassToken(_config?.tracked_button_class) ? "single" : "all");

  if (mode === "none") return false;
  if (mode === "all") return true;

  const tracked = normalizeClassToken(_config?.tracked_button_class);
  if (!tracked) return false;
  if (!el?.classList) return false;
  return el.classList.contains(tracked);
}

function elementLabel(el, tag) {
  const raw =
    el.innerText?.trim() ||
    el.value ||
    el.getAttribute("aria-label") ||
    el.getAttribute("title") ||
    tag;
  return String(raw).slice(0, 100);
}

function isFileLink(href, hasDownloadAttr) {
  if (hasDownloadAttr) return true;
  if (!href) return false;
  try {
    const pathname = new URL(href, window.location.href).pathname;
    const ext = pathname
      .split(".")
      .pop()
      ?.toLowerCase();
    return !!ext && FILE_EXTENSIONS.includes(ext);
  } catch {
    return false;
  }
}

function onDocumentClick(e) {
  const el =
    e.target && e.target.closest
      ? e.target.closest("a, button, input")
      : e.target;
  if (!el) return;
  const tag = el.tagName || "";

  const isButton =
    tag === "BUTTON" ||
    (tag === "INPUT" && ["button", "submit"].includes(el.type));
  const isLink = tag === "A";
  if (!isButton && !isLink) return;

  const label = elementLabel(el, tag);

  if (isLink) {
    const href = el.getAttribute("href");
    const hasDownload = el.hasAttribute("download");
    if (isFileLink(href, hasDownload)) {
      let fileName = el.getAttribute("download") || "";
      if (!fileName && href) {
        try {
          fileName =
            new URL(href, window.location.href).pathname.split("/").pop() || "";
        } catch {
          fileName = "";
        }
      }
      send("file_download", { outcome: "success", element: fileName || label });
      return;
    }
    send("link_click", { outcome: "success", element: label });
    return;
  }

  if (!shouldLogButtonClick(el)) return;

  send("button_click", { outcome: "success", element: label });
}

function onDocumentChange(e) {
  const el = e.target;
  const tag = el.tagName || "";
  if (!["INPUT", "SELECT", "TEXTAREA"].includes(tag)) return;

  const fieldType = el.type || tag.toLowerCase();

  const fieldName =
    el.getAttribute("name") ||
    el.getAttribute("id") ||
    el.getAttribute("aria-label") ||
    fieldType;

  send("input_change", {
    outcome: "success",
    element: `${fieldName} (${fieldType})`.slice(0, 100),
  });
}

function onDocumentSubmit(e) {
  const form = e.target;
  // DynamicForm logs its own submit + API access — skip to avoid duplicates
  if (form.hasAttribute("data-gg-dynamic-form")) return;

  const formName =
    form.getAttribute("name") ||
    form.getAttribute("id") ||
    form.getAttribute("aria-label") ||
    "form";

  send("form_submit", {
    outcome: "success",
    form_name: formName.slice(0, 100),
  });
}

function attachListeners() {
  if (_listeners.length) return;

  const clickHandler = onDocumentClick;
  const submitHandler = onDocumentSubmit;
  const changeHandler = onDocumentChange;

  document.addEventListener("click", clickHandler, true);
  document.addEventListener("submit", submitHandler, true);
  document.addEventListener("change", changeHandler, true);

  _listeners = [
    { type: "click", handler: clickHandler, capture: true },
    { type: "submit", handler: submitHandler, capture: true },
    { type: "change", handler: changeHandler, capture: true },
  ];
}

function attachPageHideHandler() {
  if (_pageHideHandler) return;
  _pageHideHandler = () => flushQueueBeacon();
  window.addEventListener("pagehide", _pageHideHandler);
}

function detachPageHideHandler() {
  if (!_pageHideHandler) return;
  window.removeEventListener("pagehide", _pageHideHandler);
  _pageHideHandler = null;
}

// SPA route-change tracking: hooks popstate/hashchange and monkey-patches
// history.pushState/replaceState so every client-side navigation logs a page_view.
let _routeChangeHandler = null;
let _originalPushState = null;
let _originalReplaceState = null;
let _lastTrackedPath = null;

function currentRoutePath() {
  return (
    window.location.pathname + window.location.search + window.location.hash
  );
}

function onRouteChange() {
  const path = currentRoutePath();
  if (path === _lastTrackedPath) return;
  _lastTrackedPath = path;
  send("page_view", { outcome: "success" });
}

function attachRouteChangeListener() {
  if (_routeChangeHandler) return;
  _lastTrackedPath = currentRoutePath();
  _routeChangeHandler = onRouteChange;

  _originalPushState = history.pushState;
  _originalReplaceState = history.replaceState;

  history.pushState = function(...args) {
    const result = _originalPushState.apply(this, args);
    window.dispatchEvent(new Event("gg-audit-locationchange"));
    return result;
  };
  history.replaceState = function(...args) {
    const result = _originalReplaceState.apply(this, args);
    window.dispatchEvent(new Event("gg-audit-locationchange"));
    return result;
  };

  window.addEventListener("gg-audit-locationchange", _routeChangeHandler);
  window.addEventListener("popstate", _routeChangeHandler);
  window.addEventListener("hashchange", _routeChangeHandler);
}

function detachRouteChangeListener() {
  if (!_routeChangeHandler) return;
  window.removeEventListener("gg-audit-locationchange", _routeChangeHandler);
  window.removeEventListener("popstate", _routeChangeHandler);
  window.removeEventListener("hashchange", _routeChangeHandler);
  if (_originalPushState) history.pushState = _originalPushState;
  if (_originalReplaceState) history.replaceState = _originalReplaceState;
  _routeChangeHandler = null;
  _originalPushState = null;
  _originalReplaceState = null;
  _lastTrackedPath = null;
}

function exposeGlobalHooks() {
  window.ggAuditLog = ggAuditLog;
  window.ggAuditLogRecord = ggAuditLogRecord;
}

function removeGlobalHooks() {
  delete window.ggAuditLog;
  delete window.ggAuditLogRecord;
}

export function logEvent(action, extra = {}) {
  send(action, extra);
}

export function logForcedLogout(reason) {
  send("logout", { outcome: "success", reason });
}

// Site builders can emit custom audit events from global JS:
//   window.ggAuditLog('button_click', { element: 'opened billing tab' })
export function ggAuditLog(action, extra = {}) {
  logEvent(action, extra);
}

// Convenience helper for record-level hints (metadata only, no PHI values):
//   window.ggAuditLogRecord('page_view', 'patient', '12345')
export function ggAuditLogRecord(action, resourceType, resourceId, extra = {}) {
  const hint = `record:${resourceType}:${resourceId}`;
  logEvent(action, {
    ...extra,
    element: extra.element ? `${hint}; ${extra.element}` : hint,
  });
}

export function initAuditLogger(
  siteId,
  hipaaConfig,
  { logInitialPageView = true } = {},
) {
  if (!hipaaConfig || !hipaaConfig.enabled) return;

  if (_initialized && _siteId === siteId) return;

  if (_initialized) destroyAuditLogger();

  _siteId = siteId;
  _config = hipaaConfig;
  _sessionId = getOrCreateSessionId();
  _initialized = true;

  attachListeners();
  attachPageHideHandler();
  attachRouteChangeListener();
  exposeGlobalHooks();
  flushQueue();

  if (logInitialPageView) {
    send("page_view", { outcome: "success" });
  }
}

export function destroyAuditLogger() {
  flushQueueBeacon();
  _listeners.forEach(({ type, handler, capture }) => {
    document.removeEventListener(type, handler, capture);
  });
  _listeners = [];
  detachPageHideHandler();
  detachRouteChangeListener();
  removeGlobalHooks();
  _config = null;
  _siteId = null;
  _initialized = false;
}
